import React, { createContext, useContext, useState, useEffect, useEffectEvent, useRef } from 'react';
import { useAuth } from '@clerk/react';
import { cloudService } from '@/services/cloudService';
import type { Cloud, CloudRegion } from '@/types/cloud';
import { COMING_SOON_REGIONS } from '@/types/cloud';
import { cloudKeyFromName, type CloudKey } from '@/lib/locality';

/**
 * The cloud/region VIEW FILTER (sidebar selector). Pure client-side context —
 * cloud is NOT a scope in the data model; the API's governance (mount
 * locality flags, cloud-aware master-ESP resolution) stays the enforcement
 * layer. 'all' disables filtering. Cloud-NEUTRAL resources (R2, Local,
 * HashiCorp Vault ESPs, postgres-only catalogs) are always visible in every
 * cloud view — pages pass `undefined` cloud for those.
 */
export type CloudFilterSelection = {
  cloud: CloudKey | 'all';
  regionId: string | 'all';
};

const CLOUD_FILTER_STORAGE_KEY = 'nexus.cloudFilter';

const loadStoredCloudFilter = (): CloudFilterSelection => {
  try {
    const raw = localStorage.getItem(CLOUD_FILTER_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.cloud === 'string' && typeof parsed.regionId === 'string') {
        return parsed;
      }
    }
  } catch { /* corrupted storage — fall through */ }
  return { cloud: 'all', regionId: 'all' };
};

interface CloudContextType {
  clouds: Cloud[];
  cloudRegions: CloudRegion[];
  comingSoonRegions: typeof COMING_SOON_REGIONS;
  isLoading: boolean;
  error: string | null;
  getCloudById: (cloudId: string) => Cloud | undefined;
  getRegionById: (regionId: string) => CloudRegion | undefined;
  getRegionsForCloud: (cloudId: string) => CloudRegion[];
  getEffectiveDefaultRegion: (
    organizationDefaultId?: string,
    projectDefaultId?: string,
    environmentDefaultId?: string
  ) => CloudRegion | null;
  refreshClouds: () => Promise<void>;
  /** Cloud/region view filter (sidebar selector). */
  cloudFilter: CloudFilterSelection;
  setCloudFilter: (selection: CloudFilterSelection) => void;
  /**
   * Does a resource pass the current view filter? Pass what the resource
   * KNOWS about itself; unknown fields never exclude it:
   * - cloud undefined → cloud-neutral → always visible.
   * - regionId undefined → visible under any region of its cloud.
   */
  matchesCloudFilter: (resource: { cloud?: CloudKey; regionId?: string }) => boolean;
  /** CloudKey for a clouds-table row id (via the loaded clouds list). */
  cloudKeyForCloudId: (cloudId?: string) => CloudKey | undefined;
  /** CloudKey for a cloud_regions-table row id. */
  cloudKeyForRegionId: (regionId?: string) => CloudKey | undefined;
}

const CloudContext = createContext<CloudContextType | null>(null);

export const useCloud = () => {
  const context = useContext(CloudContext);
  if (!context) {
    throw new Error('useCloud must be used within a CloudProvider');
  }
  return context;
};

interface CloudProviderProps {
  children: React.ReactNode;
}

export const CloudProvider = ({ children }: CloudProviderProps) => {
  const [clouds, setClouds] = useState<Cloud[]>([]);
  const [cloudRegions, setCloudRegions] = useState<CloudRegion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cloudFilter, setCloudFilterState] = useState<CloudFilterSelection>(loadStoredCloudFilter);

  const setCloudFilter = (selection: CloudFilterSelection) => {
    setCloudFilterState(selection);
    try { localStorage.setItem(CLOUD_FILTER_STORAGE_KEY, JSON.stringify(selection)); } catch { /* ignore */ }
  };

  // Load clouds and regions on mount
  const retryTimer = useRef<number | null>(null);
  const loadClouds = async (attempt = 0) => {
    if (retryTimer.current !== null) {
      window.clearTimeout(retryTimer.current);
      retryTimer.current = null;
    }
    setIsLoading(true);
    setError(null);

    try {
      const [loadedClouds, loadedRegions] = await Promise.all([
        cloudService.getClouds(),
        cloudService.getCloudRegions(),
      ]);

      // Alphabetical everywhere (AWS, Azure, GCP; regions by name) — the API returns
      // seed order, which put GCP first in every picker.
      const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);
      setClouds([...loadedClouds].sort(byName));
      setCloudRegions([...loadedRegions].sort(byName));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load cloud data');
      // A single failed fetch used to pin the static Azure-only mock list here,
      // silently erasing the real clouds (GCP/AWS) from every picker until a
      // lucky reload. Keep whatever data we have and retry with backoff instead.
      if (attempt < 4) {
        retryTimer.current = window.setTimeout(
          () => { void loadClouds(attempt + 1); },
          2000 * 2 ** attempt,
        );
      }
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => () => {
    if (retryTimer.current !== null) window.clearTimeout(retryTimer.current);
  }, []);

  // Wait for Clerk to hydrate before the first fetch, and refetch on
  // sign-in/out transitions. Without this, a cold visit raced ApiAuthBridge:
  // the first /clouds request went out with NO token, 401'd, and the catch
  // branch pinned the static mock list (Azure-first) until a lucky reload —
  // seen live on the staging portal's first boot (2026-07-31).
  const { isLoaded: authLoaded, isSignedIn } = useAuth();
  // Reload on sign-in/out only — not whenever loadClouds is recreated (it is, every render).
  const loadCloudsForAuthChange = useEffectEvent(() => { void loadClouds(); });
  // Signed in only: /clouds/ and /cloud-regions/ need a session, so a signed-out page (sign-in,
  // sign-up) asking for them just got 401s (2026-10-08).
  useEffect(() => {
    if (!authLoaded || !isSignedIn) return;
    loadCloudsForAuthChange();
  }, [authLoaded, isSignedIn]);

  const getCloudById = (cloudId: string): Cloud | undefined => {
    return clouds.find(c => c.id === cloudId);
  };

  const getRegionById = (regionId: string): CloudRegion | undefined => {
    return cloudRegions.find(r => r.id === regionId);
  };

  const getRegionsForCloud = (cloudId: string): CloudRegion[] => {
    return cloudRegions.filter(r => r.cloudId === cloudId);
  };

  /**
   * Get the effective default cloud region based on the hierarchy.
   * Lower level settings override higher level (Environment > Project > Organization).
   */
  const getEffectiveDefaultRegion = (
    organizationDefaultId?: string,
    projectDefaultId?: string,
    environmentDefaultId?: string
  ): CloudRegion | null => {
    // Environment default takes precedence, then project, then organization
    const effectiveId = environmentDefaultId || projectDefaultId || organizationDefaultId;

    if (effectiveId) {
      const region = getRegionById(effectiveId);
      if (region) return region;
    }

    // Fall back to the first region the API returned
    return cloudRegions.length > 0 ? cloudRegions[0] : null;
  };

  const refreshClouds = async (): Promise<void> => {
    await loadClouds();
  };

  const cloudKeyForCloudId = (cloudId?: string): CloudKey | undefined => {
    if (!cloudId) return undefined;
    const cloud = getCloudById(cloudId);
    return cloudKeyFromName(cloud?.name || cloud?.label);
  };

  const cloudKeyForRegionId = (regionId?: string): CloudKey | undefined => {
    if (!regionId) return undefined;
    const region = getRegionById(regionId);
    if (!region) return undefined;
    return cloudKeyForCloudId(region.cloudId) ?? cloudKeyFromName(region.name || region.label);
  };

  const matchesCloudFilter = (resource: { cloud?: CloudKey; regionId?: string }): boolean => {
    if (cloudFilter.cloud === 'all') return true;
    // Cloud-neutral resources are visible in every cloud view.
    if (!resource.cloud) return true;
    if (resource.cloud !== cloudFilter.cloud) return false;
    if (cloudFilter.regionId === 'all') return true;
    // Region unknown → can't disprove membership; keep it visible.
    if (!resource.regionId) return true;
    return resource.regionId === cloudFilter.regionId;
  };

  return (
    <CloudContext.Provider
      value={{
        clouds,
        cloudRegions,
        comingSoonRegions: COMING_SOON_REGIONS,
        isLoading,
        error,
        getCloudById,
        getRegionById,
        getRegionsForCloud,
        getEffectiveDefaultRegion,
        refreshClouds,
        cloudFilter,
        setCloudFilter,
        matchesCloudFilter,
        cloudKeyForCloudId,
        cloudKeyForRegionId,
      }}
    >
      {children}
    </CloudContext.Provider>
  );
};
